import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { FileUpload, FileUploadItem, Grid } from "@sumprio/ui";
import { useFileUploadWithProgress } from "./useFileUploadWithProgress";

export interface FileUploadCompletedEntry {
  id: string;
  file: File;
}

export interface FileUploadWithProgressRef {
  getCompletedFile: () => File | null;
  getCompletedFiles: () => File[];
  getCompletedEntries: () => FileUploadCompletedEntry[];
  reset: () => void;
}

export interface FileUploadWithProgressProps {
  label?: string;
  required?: boolean;
  mainText?: string;
  browseText?: string;
  supportingText?: string;
  acceptedFileTypes: string[];
  maxFileSizeBytes?: number;
  maxFiles?: number;
  singleFileMode?: boolean;
  onValidationError?: (_message: string) => void;
  onCompletedChange?: (_hasCompleted: boolean) => void;
  onFilesChange?: (_files: { id: string; status: string }[]) => void;
  renderCompletedExtras?: (_fileId: string) => React.ReactNode;
  width?: "full" | number | string;
  maxDimensions?: string;
}

const DEFAULT_MAIN_TEXT = "Drag and drop files here or";
const DEFAULT_BROWSE_TEXT = "Browse to upload";

const FileUploadWithProgress = forwardRef<FileUploadWithProgressRef, FileUploadWithProgressProps>(
  function FileUploadWithProgress(
    {
      label,
      required,
      mainText = DEFAULT_MAIN_TEXT,
      browseText = DEFAULT_BROWSE_TEXT,
      supportingText,
      acceptedFileTypes,
      maxFileSizeBytes,
      maxFiles,
      singleFileMode = true,
      onValidationError,
      onCompletedChange,
      onFilesChange,
      renderCompletedExtras,
      width = "full",
      maxDimensions,
    },
    ref
  ) {
    const {
      files,
      handleFileSelect,
      handleFileDelete,
      hasCompletedFile,
      atFileLimit,
      resetFiles,
      getCompletedFile,
      getCompletedFiles,
      fileMapRef,
    } = useFileUploadWithProgress({
      acceptedFileTypes,
      maxFileSizeBytes,
      maxFiles,
      singleFileMode,
      onValidationError,
    });

    const uploadRootRef = useRef<HTMLDivElement | null>(null);
    const useCustomFileList = typeof renderCompletedExtras === "function";

    const syncNativeFileInput = useCallback(() => {
      const input = uploadRootRef.current?.querySelector<HTMLInputElement>("input[type=file]");
      if (!input) return;
      input.multiple = !singleFileMode;
    }, [singleFileMode]);

    useLayoutEffect(() => {
      syncNativeFileInput();
    }, [syncNativeFileInput, files.length]);

    const handleFileSelectAndResetInput = useCallback(
      (fileList: FileList) => {
        handleFileSelect(fileList);
        const input = uploadRootRef.current?.querySelector<HTMLInputElement>("input[type=file]");
        if (input) input.value = "";
      },
      [handleFileSelect]
    );

    useEffect(() => {
      onCompletedChange?.(hasCompletedFile);
    }, [hasCompletedFile, onCompletedChange]);

    useEffect(() => {
      onFilesChange?.(files.map((f) => ({ id: f.id, status: f.status })));
    }, [files, onFilesChange]);

    const getCompletedEntries = useCallback((): FileUploadCompletedEntry[] => {
      return files
        .filter((f) => f.status === "completed")
        .map((f) => {
          const file = fileMapRef.current.get(f.id);
          return file ? { id: f.id, file } : null;
        })
        .filter((entry): entry is FileUploadCompletedEntry => entry !== null);
    }, [files, fileMapRef]);

    useImperativeHandle(ref, () => ({
      getCompletedFile,
      getCompletedFiles,
      getCompletedEntries,
      reset: resetFiles,
    }));

    return (
      <Grid display="flex" flexDirection="column" gap="md" width="100%">
        <FileUpload
          ref={uploadRootRef}
          label={label}
          required={required}
          mainText={mainText}
          browseText={browseText}
          supportingText={supportingText}
          acceptedFileTypes={acceptedFileTypes}
          maxDimensions={maxDimensions}
          onFileSelect={handleFileSelectAndResetInput}
          files={useCustomFileList ? [] : files}
          onFileDelete={useCustomFileList ? undefined : handleFileDelete}
          showDropZone
          disabled={atFileLimit}
          width={width}
        />
        {useCustomFileList
          ? files.map((file) => (
              <Grid key={file.id} display="flex" flexDirection="column" gap="md" width="100%">
                <FileUploadItem
                  fileName={file.fileName}
                  fileSize={file.fileSize}
                  status={file.status}
                  progress={file.progress}
                  statusMessage={file.statusMessage}
                  fileTypeIcon={file.fileTypeIcon}
                  width="100%"
                  onCancel={
                    file.status === "inProgress" ? () => handleFileDelete(file.id) : undefined
                  }
                  onDelete={
                    file.status === "completed" || file.status === "error"
                      ? () => handleFileDelete(file.id)
                      : undefined
                  }
                  onRetry={undefined}
                />
                {file.status === "completed" ? renderCompletedExtras?.(file.id) : null}
              </Grid>
            ))
          : null}
      </Grid>
    );
  }
);

export default FileUploadWithProgress;
